import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, Package, Search } from "lucide-react";
import { STORE_API, imgUrl, inr } from "../store/CartContext";

const STEPS = [["paid", "Order confirmed"], ["packed", "Packed"], ["shipped", "Shipped"], ["delivered", "Delivered"]];
const LABEL = { pending_payment: "Awaiting payment", paid: "Confirmed", packed: "Packed", shipped: "Shipped", delivered: "Delivered", cancelled: "Cancelled" };

export function OrderView({ order, itemExtra, actions }) {
  const idx = STEPS.findIndex(([s]) => s === order.status);
  return (
    <div data-testid="order-view" className="space-y-5 rounded-3xl bg-white p-5 sm:p-7">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-xs uppercase tracking-wider text-[#5E5675]">Order</p>
          <p data-testid="order-number" className="font-heading text-2xl font-bold">{order.order_number}</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {actions}
          <span data-testid="order-status" className="rounded-full bg-[#3915AA]/10 px-3 py-1 text-xs font-bold uppercase tracking-wider text-[#3915AA]">{LABEL[order.status]}</span>
        </div>
      </div>
      {order.status !== "cancelled" && order.status !== "pending_payment" && (
        <div className="grid grid-cols-4 gap-1">
          {STEPS.map(([s, l], i) => (
            <div key={s} className="text-center">
              <div className={`h-1.5 rounded-full ${i <= idx ? "bg-[#FC5106]" : "bg-[#3915AA]/10"}`} />
              <p className={`mt-2 text-[11px] font-semibold ${i <= idx ? "text-[#1E0A5C]" : "text-[#5E5675]/60"}`}>{l}</p>
            </div>
          ))}
        </div>
      )}
      {order.tracking_number && <p data-testid="order-tracking" className="rounded-xl bg-[#F7F5FF] p-3 text-sm">Shipped via <b>{order.courier || "courier"}</b> · Tracking no. <b>{order.tracking_number}</b></p>}
      <div className="space-y-3">
        {order.items.map((i) => (
          <div key={i.variant_id} className="flex gap-3 text-sm">
            <img src={imgUrl(i.image)} alt="" className="h-14 w-14 rounded-lg object-cover" />
            <div className="flex-1"><p className="font-semibold">{i.name}</p><p className="text-xs text-[#5E5675]">{i.variant_label} × {i.qty}</p>{itemExtra?.(i)}</div>
            <p className="font-semibold">{inr(i.price * i.qty)}</p>
          </div>
        ))}
      </div>
      <div className="grid gap-4 border-t border-[#3915AA]/10 pt-4 text-sm sm:grid-cols-2">
        <div>
          <p className="mb-1 text-xs font-bold uppercase tracking-wider text-[#3915AA]/70">Delivering to</p>
          <p>{order.customer.name}</p>
          <p className="text-[#5E5675]">{order.address.line1}{order.address.line2 ? `, ${order.address.line2}` : ""}, {order.address.city}, {order.address.state} – {order.address.pincode}</p>
        </div>
        <div className="space-y-1 sm:text-right">
          <p className="text-[#5E5675]">Subtotal {inr(order.subtotal)}</p>
          <p className="text-[#5E5675]">Delivery {order.shipping ? inr(order.shipping) : "FREE"}</p>
          <p className="text-base font-bold">Total {inr(order.total)}</p>
        </div>
      </div>
    </div>
  );
}

async function fetchOrder(number, phone) {
  const { data } = await axios.get(`${STORE_API}/orders/track`, { params: { order_number: number, phone } });
  return data;
}

export function OrderSuccess() {
  const { orderNumber } = useParams();
  const [params] = useSearchParams();
  const [order, setOrder] = useState(null);
  useEffect(() => { fetchOrder(orderNumber, params.get("phone") || "").then(setOrder).catch(() => setOrder(false)); }, [orderNumber, params]);
  return (
    <div data-testid="order-success-page" className="min-h-screen bg-[#F7F5FF] text-[#1E0A5C]">
      <section className="container-custom max-w-3xl py-12">
        <div className="mb-6 text-center">
          <CheckCircle2 className="mx-auto h-14 w-14 text-green-500" />
          {!order || ["paid", "pending_payment"].includes(order.status) ? <>
            <h1 className="mt-3 font-heading text-3xl font-bold">Thank you! Your order is placed.</h1>
            <p className="mt-1 text-sm text-[#5E5675]">We'll WhatsApp you when it ships. Save your order number to track it.</p>
          </> : <h1 data-testid="order-status-heading" className="mt-3 font-heading text-3xl font-bold">{{ packed: "Your order is packed", shipped: "Your order is on its way!", delivered: "Your order has been delivered", cancelled: "This order was cancelled" }[order.status]}</h1>}
        </div>
        {order === null ? <div className="flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#FC5106]" /></div> : order ? <OrderView order={order} /> : <p className="text-center">Order {orderNumber} placed. <Link to="/track-order" className="font-bold text-[#FC5106]">Track it here</Link>.</p>}
        <div className="mt-6 text-center"><Link to="/shop" className="rounded-full bg-[#FC5106] px-6 py-3 text-sm font-bold text-white">Continue shopping</Link></div>
      </section>
    </div>
  );
}

export default function TrackOrder() {
  const [number, setNumber] = useState("");
  const [phone, setPhone] = useState("");
  const [order, setOrder] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try { setOrder(await fetchOrder(number, phone)); } catch { setOrder(null); setErr("No order found with that order number and phone."); } finally { setBusy(false); }
  };
  return (
    <div data-testid="track-order-page" className="min-h-screen bg-[#F7F5FF] text-[#1E0A5C]">
      <section className="container-custom max-w-3xl py-12">
        <Package className="h-10 w-10 text-[#FC5106]" />
        <h1 className="mt-3 font-heading text-3xl font-bold">Track your order</h1>
        <form onSubmit={submit} className="mt-6 grid gap-3 rounded-3xl bg-white p-5 sm:grid-cols-[1fr_1fr_auto]">
          <input required value={number} onChange={(e) => setNumber(e.target.value.toUpperCase())} placeholder="Order number (e.g. BNF1001)" data-testid="track-order-id-input" className="rounded-xl border border-[#3915AA]/15 bg-[#F7F5FF] px-3.5 py-3 text-sm outline-none focus:border-[#FC5106]" />
          <input required value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone number" inputMode="tel" data-testid="track-phone-input" className="rounded-xl border border-[#3915AA]/15 bg-[#F7F5FF] px-3.5 py-3 text-sm outline-none focus:border-[#FC5106]" />
          <button disabled={busy} data-testid="track-submit-button" className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#3915AA] px-5 py-3 text-sm font-bold text-white">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Track</button>
        </form>
        {err && <p data-testid="track-error" className="mt-3 text-sm text-red-500">{err}</p>}
        {order && <div className="mt-6"><OrderView order={order} /></div>}
      </section>
    </div>
  );
}
